import queryString from 'query-string'
import memoize from 'lodash/memoize'
import findLastIndex from 'lodash/findLastIndex'
import get from 'lodash/get'
import unescape from 'lodash/unescape'

import { startPolling } from '../../../../ChatWidget'
import UserFriendlyError from 'helpers/UserFriendlyError'

import {
  makeGetSession,
  makeFetchAnswer,
  makeGetRawHistory,
  makeGetHistory,
  makeGetBotResponse,
  makeGetConversationId,
  getSessionData,
  setSessionData
} from './shared'

const nuanceAdapter = config => {
  const { botId, botName, apiUrl, userId, pushEvent, language, customer } = config
  const botUrl = apiUrl || process.env.REACT_APP_BOT_URL
  sessionStorage.setItem('bot_name', botName || botId)
  sessionStorage.setItem('bot_id', botId)

  const sessionUrl = `${botUrl}/session`
  const historyUrl = `${botUrl}/history`
  const conversationIdUrl = `${botUrl}/conversation-id`
  const engageUrl = `${botUrl}/engage`
  const messageUrl = `${botUrl}/message`
  const dataPassUrl= `${botUrl}/data-pass`

  const getSession = memoize(
    makeGetSession({ sessionUrl, userId, getSessionData, setSessionData, botId })
  )
  const fetchAnswer = makeFetchAnswer({ botId, botUrl, getSession })
  const getRawHistory = makeGetRawHistory({ getSession, historyUrl })
  const getHistory = makeGetHistory({ getRawHistory })
  const getConversationId = makeGetConversationId({ getSession, conversationIdUrl })
  const getBotResponse = makeGetBotResponse({ fetchAnswer })

  const trackEvent = makeTrackEvent({ getConversationId, pushEvent, botName, botId })
  const isLiveSessionEstablished = makeIsLiveSessionEstablished({ getLiveChatData })
  const updateHistory = makeUpdateHistory({ getSession, historyUrl })
  const historyAddUserMsg = makeHistoryAddUserMsg({ updateHistory })
  const historyAddResponseMsg = makeHistoryAddResponseMsg({ updateHistory })
  const fetchIsCurrentCustomer = makeFetchIsCurrentCustomer({ getHistory })
  const getStatus = makeGetStatus({ fetchIsCurrentCustomer })
  const startLiveChat = makeStartLiveChat({
    getStatus,
    engageUrl,
    getLiveChatData,
    setLiveChatData,
    language,
    customer
  })
  const sendLiveMessage = makeSendLiveMessage({ messageUrl, getLiveChatData })
  const handleUserMessage = makeHandleUserMessage({
    getBotResponse,
    historyAddUserMsg,
    sendLiveMessage,
    isLiveSessionEstablished,
    trackEvent,
    removeLiveChataData
  })
  const sendDataPass = makeSendDataPass({
    getRawHistory,
    dataPassUrl,
    language,
    getStatus,
    customer
  })
  const poll = makePoll({
    messageUrl,
    sendDataPass,
    startLiveChat,
    removeLiveChataData,
    historyAddResponseMsg,
    trackEvent
  })

  return {
    getBotResponse: handleUserMessage,
    getHistory,
    getConversationId,
    poll,
    translateErrorMessage,
    init: () => {
      if (isLiveSessionEstablished()) {
        startPolling()
      }
    }
  }
}

const makeHandleUserMessage = (config) => async (...args) => {
  const {
    getBotResponse,
    historyAddUserMsg,
    sendLiveMessage,
    isLiveSessionEstablished,
    trackEvent,
    removeLiveChataData
  } = config
  const [question] = args

  if (isRestartKeyword(question)) {
    removeLiveChataData()
    return getBotResponse(...args)
  }

  if (isLiveSessionEstablished()) {
    await sendLiveMessage(question)
    historyAddUserMsg(question)
    trackEvent({ reply: question, direction: 'in' })
    return { poll: true }
  }

  if (isAgentTransfer(question)) {
    historyAddUserMsg(question)
    return { poll: true }
  }

  return getBotResponse(...args)
}

const isAgentTransfer = reply => (
  reply && reply.trim().toLowerCase() === 'agent'
)

const isRestartKeyword = reply => (
  reply && reply.trim().toLowerCase() === 'restart'
)

const makeIsLiveSessionEstablished = ({ getLiveChatData }) => () => {
  const { engagementID } = getLiveChatData()
  return !!engagementID
}

const makeStartLiveChat = config => async () => {
  const {
    getLiveChatData,
    setLiveChatData,
    engageUrl,
    language,
    customer,
    getStatus
  } = config
  const { engagementID, customerID } = getLiveChatData()
  if (engagementID && customerID) {
    return { engagementID, customerID }
  }

  const status = await getStatus()
  const query = queryString.stringify({
    status,
    lang: language,
    client: customer.replace('-staging', '')
  })

  const response = await fetch(`${engageUrl}?${query}`, {
    method: 'POST'
  })
  const json = await response.json()

  if (!response.ok) {
    throw new Error(json.error.message)
  }

  setLiveChatData(json)
  return json
}

const makeFetchIsCurrentCustomer = ({ getHistory }) => async () => {
  const history = await getHistory()

  const currCustIndex = findLastIndex(history, item => (
    get(item, 'userMessage') === 'Current Customer'
  ))
  const newCustIndex = findLastIndex(history, item => (
    get(item, 'userMessage') === 'Become a Customer'
  ))

  return currCustIndex > newCustIndex
}

const makeUpdateHistory = ({ getSession, historyUrl }) => async payload => {
  const { sessionId, sessionToken } = await getSession()
  const query = queryString.stringify({
    session: sessionId,
    token: sessionToken
  })
  return fetch(`${historyUrl}?${query}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

const makeHistoryAddUserMsg = ({ updateHistory }) => msg => (
  updateHistory({ userMessage: msg })
)

const makeHistoryAddResponseMsg = ({ updateHistory }) => msg => (
  updateHistory({ botMessage: { output: msg } })
)

const makePoll = config => async () => {
  const {
    messageUrl,
    sendDataPass,
    startLiveChat,
    removeLiveChataData,
    historyAddResponseMsg,
    trackEvent
  } = config
  const { engagementID, customerID } = await startLiveChat()

  const query = queryString.stringify({
    engagementID,
    customerID,
  })

  const response = await fetch(`${messageUrl}?${query}`)
  const json = await response.json()
  if (!response.ok) {
    console.error(json.message)
    removeLiveChataData()
    throw new Error('Live chat failed')
  }

  const { messages } = json

  if (isSessionClosed(messages)) {
    removeLiveChataData()
    throw new UserFriendlyError('Live chat session end')
  }

  const agentAssignedEvent = findAgentAssignedEvent(messages)
  if (agentAssignedEvent) {
    await sendDataPass({ engagementID, agentID: agentAssignedEvent.agentID })
  }

  const displayableMessages = filterDisplayableMessages(messages)

  displayableMessages.forEach(msg => {
    historyAddResponseMsg(msg.messageText)
    trackEvent({ direction: 'out', reply: msg.messageText })
  })

  return displayableMessages.map(msg => ({
    response: msg.messageText, timestamp: parseInt(msg.messageTimestamp, 10)
  }))
}

const makeSendDataPass = ({ getRawHistory, dataPassUrl, language, getStatus, customer }) => async ({
  agentID,
  engagementID
}) => {
  const status = await getStatus()
  const history = await getRawHistory()
  const botHistory = history.map((item) => {
    const {
      payload: { userMessage, botMessage }
    } = item
    if (userMessage) {
      return {
        userMessage: { output: userMessage }
      }
    }
    return {
      botMessage: {
        ...botMessage,
        output: unescapeEntities(botMessage.output)
      }
    }
  })

  const body = {
    agentID,
    botHistory,
    engagementID,
    lang: language,
    status,
    client: customer.replace('-staging', '')
  }

  return fetch(dataPassUrl, {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

const makeSendLiveMessage = ({ messageUrl, getLiveChatData }) => msg => {
  const { engagementID } = getLiveChatData()
  fetch(messageUrl, {
    method: 'POST',
    body: JSON.stringify({
      engagementID,
      messageText: msg
    })
  })
}

const unescapeEntities = string => (
  unescape(string).replaceAll('&nbsp;', ' ')
)

const setLiveChatData = ({ engagementID, customerID }) => {
  localStorage.setItem('engagementID', engagementID)
  localStorage.setItem('customerID', customerID)
}

const getLiveChatData = () => ({
  engagementID: localStorage.getItem('engagementID'),
  customerID: localStorage.getItem('customerID')
})

const removeLiveChataData = () => {
  localStorage.removeItem('engagementID')
  localStorage.removeItem('customerID')
}

const makeTrackEvent = config => async payload => {
  const { pushEvent, getConversationId, botName, botId } = config
  const conversationId = await getConversationId()
  return pushEvent('liveInteraction', {
    ...payload,
    botId,
    session_id: conversationId,
    botName: botName || botId,
  })
}

const makeGetStatus = ({ fetchIsCurrentCustomer }) => async () => {
  const isCurrentCustomer = await fetchIsCurrentCustomer()
  return isCurrentCustomer ? 'care' : 'sales'
}

const isSessionClosed = messages => messages.some(msg => msg.state === 'closed')

const findAgentAssignedEvent = messages => messages.find(msg => msg.state === 'assigned')

const filterDisplayableMessages = messages => (
  messages.filter(msg =>
    (msg.messageType === 'chatLine' && msg.hasOwnProperty('agent.alias'))
      || (msg.waitTime && msg.waitTime !== '-1')
  )
)

const translateErrorMessage = msg => ({
  'off business hours': `Chat Service is currently unavailable.
This service is available 8AM-11:45PM EST Monday through Sunday. Please try again later.`,
  'service unavailable': `We are experiencing high volume on the support service and are not
able to process your request at the moment. please try later.`,
}[msg])

export default nuanceAdapter



// WEBPACK FOOTER //
// ./src/App/services/adapters/botapi/nuance.js